import { components } from "@/api-schema";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { DEACTIVATED_USER_NAME_CLASS_NAME, isUserDeactivated } from "@/common/helpers/user-deactivated";
import { cn } from "@/common/helpers/utils";
import { DeviceType } from "@/common/hooks/use-device";
import { ImplementedAccessorColumnDef, ImplementedDisplayColumnDef } from "@/components/data-table/types";
import { Button, ButtonVariant } from "@/components/ui/button";
import { AvatarClickAction, UserAvatar } from "@/components/user-avatar/user-avatar";
import { UserName } from "@/components/user-item/user-name";
import i18next from "i18next";
import { MailIcon, PhoneIcon, SmartphoneIcon } from "lucide-react";
import { BranchWithAddress } from "./_components/branch-with-address";
import { commonColumns } from "./_constants/common-columns";

type UserTableType = components["schemas"]["User"];
type Branch = components["schemas"]["Branch"];

interface NameAndContactInfo {
  name: string;
  email: string | null | undefined;
  telephone: string | null | undefined;
  mobile: string | null | undefined;
}

interface ContactInfo {
  email: string | null | undefined;
  telephone: string | null | undefined;
  mobile: string | null | undefined;
}

interface DepartmentInfo {
  departments: UserTableType["departments"];
  occupations: UserTableType["occupations"];
  specialization: UserTableType["specialization"];
}

export const phonebookTableDefaultColumns = {
  columnAvatar: {
    id: "avatar",
    size: 50,
    enableSorting: false,
    visibility: [DeviceType.Tablet, DeviceType.Desktop, DeviceType.Widescreen],
    cell: ({ row }) => (
      <UserAvatar
        userAzureObjectUuid={row.original.azureObjectUuid}
        name={getFullUserName({ user: row.original, emailFallback: false, fallback: "" }) || undefined}
        clickAction={AvatarClickAction.Zoom}
        isDeactivated={isUserDeactivated(row.original)}
      />
    ),
  } as ImplementedDisplayColumnDef<UserTableType>,
  columnName: {
    id: "name",
    size: 300,
    enableSorting: true,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    header: () => i18next.t("common:Name"),
    accessorFn: (row) => getFullUserName({ user: row }),
    cell: (row) => <UserName user={row.row.original} />,
  } as ImplementedDisplayColumnDef<UserTableType>,
  columnNameAndContact: {
    id: "nameAndContact",
    size: 500,
    enableSorting: false,
    visibility: [DeviceType.Mobile, DeviceType.Tablet],
    header: () => i18next.t("phonebook:NameAndContact"),
    accessorFn: (row) => ({
      name: getFullUserName({ user: row }),
      email: row.email,
      telephone: row.telephone,
      mobile: row.mobile,
    }),
    cell: (row) => {
      const user = row.getValue();
      const isDisabled = isUserDeactivated(row.row.original);
      const email = user?.email || null;
      const telephone = user?.telephone || null;
      const mobile = user?.mobile || null;

      return (
        <div className="flex flex-col gap-2">
          <UserName user={row.row.original} />
          <div className={cn("flex flex-col gap-1", isDisabled && DEACTIVATED_USER_NAME_CLASS_NAME)}>
            {email && (
              <Button
                variant={ButtonVariant.Link}
                className="flex h-auto items-center justify-start p-0"
                disabled={isDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = `mailto:${email}`;
                }}
              >
                <MailIcon className="inline-block h-4 w-4" />
                {email}
              </Button>
            )}
            {telephone && (
              <Button
                variant={ButtonVariant.Link}
                className="flex h-auto items-center justify-start p-0"
                disabled={isDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = `tel:${telephone}`;
                }}
              >
                <PhoneIcon className="inline-block h-4 w-4" />
                {telephone}
              </Button>
            )}
            {mobile && (
              <Button
                variant={ButtonVariant.Link}
                className="flex h-auto items-center justify-start p-0"
                disabled={isDisabled}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  window.location.href = `tel:${mobile}`;
                }}
              >
                <SmartphoneIcon className="inline-block h-4 w-4" />
                {mobile}
              </Button>
            )}
          </div>
        </div>
      );
    },
  } as ImplementedDisplayColumnDef<UserTableType, NameAndContactInfo>,
  columnBranch: {
    id: "branch",
    size: 400,
    header: () => i18next.t("common:Branch"),
    enableSorting: false,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => row.mainBranch,
    cell: (row) => (
      <BranchWithAddress
        branch={row.getValue()}
        address={row.row.original.address}
        className={cn(isUserDeactivated(row.row.original) && DEACTIVATED_USER_NAME_CLASS_NAME)}
      />
    ),
  } as ImplementedAccessorColumnDef<UserTableType, Branch>,
  columnContact: {
    id: "contact",
    size: 500,
    header: () => i18next.t("common:Contact"),
    enableSorting: false,
    visibility: [DeviceType.Desktop, DeviceType.Widescreen],
    accessorFn: (row) => ({
      email: row.email,
      telephone: row.telephone,
      mobile: row.mobile,
    }),
    cell: (row) => {
      const contact = row.getValue();
      const isDisabled = isUserDeactivated(row.row.original);

      return (
        <div className={cn("flex flex-col gap-2 whitespace-nowrap", isDisabled && DEACTIVATED_USER_NAME_CLASS_NAME)}>
          {contact.email && (
            <Button
              variant={ButtonVariant.Link}
              className="flex h-auto items-center justify-start p-0"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `mailto:${contact.email}`;
              }}
            >
              <MailIcon className="inline-block h-4 w-4" />
              {contact.email}
            </Button>
          )}
          {contact.telephone && (
            <Button
              variant={ButtonVariant.Link}
              className="flex h-auto items-center justify-start p-0"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `tel:${contact.telephone}`;
              }}
            >
              <PhoneIcon className="inline-block h-4 w-4" />
              {contact.telephone}
            </Button>
          )}
          {contact.mobile && (
            <Button
              variant={ButtonVariant.Link}
              className="flex h-auto items-center justify-start p-0"
              disabled={isDisabled}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `tel:${contact.mobile}`;
              }}
            >
              <SmartphoneIcon className="inline-block h-4 w-4" />
              {contact.mobile}
            </Button>
          )}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<UserTableType, ContactInfo>,
  columnDepartment: {
    id: "department",
    size: 300,
    header: () => i18next.t("common:DepartmentAndFunction"),
    enableSorting: false,
    visibility: [DeviceType.Widescreen],
    accessorFn: (row) => ({
      departments: row.departments,
      occupations: row.occupations,
      specialization: row.specialization,
    }),
    cell: (row) => {
      const value = row.getValue();
      const isDisabled = isUserDeactivated(row.row.original);
      const department = value.departments?.at(0) || null;
      const occupation = value.occupations?.at(0) || null;
      const specialization = value.specialization || null;

      return (
        <div className={cn("flex flex-col gap-2 text-left", isDisabled && DEACTIVATED_USER_NAME_CLASS_NAME)}>
          {department && <div className="font-semibold">{department.name}</div>}
          {occupation && <div className="text-xs">{occupation.name}</div>}
          {specialization && <div className="text-xs">{specialization}</div>}
        </div>
      );
    },
  } as ImplementedAccessorColumnDef<UserTableType, DepartmentInfo>,
  columnUuid: commonColumns.columnUuid<UserTableType>(),
  columnCreatedAt: commonColumns.columnCreatedAt<UserTableType>(),
  columnUpdatedAt: commonColumns.columnUpdatedAt<UserTableType>(),
};
